import { useCallback, useEffect, useRef, useState } from 'react';
import SvgLoader from 'src/components/Common/SvgLoader';
import { useDebounce } from 'src/lib/Hooks/useDebounce';
import { TooltipTypes } from './types';

const iconSize = 18;
const messageWidth = 245;

/**
 * Determines which side of the icon to display the message component.
 * It checks where the icon is within the viewport and determines
 * which way to diplay.
 */
const getMessageOrientation = (ref: HTMLElement | null) => {
  // If the leftside of OR operator is falsey, fallback to Array containing empty object and destructure
  // default values for everything.
  const iconPos = ref?.getClientRects()[0] || {
    top: 0,
    bottom: 20,
    right: 50,
    left: 50,
    width: 18,
  };
  const { top, bottom, right, left, width } = iconPos;
  const { innerHeight, innerWidth } = window;
  const buffer = 45;
  const messageWidthWithBuffer = messageWidth + buffer;

  const iconPercentageLeft = ((left + width / 2) / innerWidth) * 100;
  const isIconMostlyCentered = iconPercentageLeft > 35 && iconPercentageLeft < 65;
  // if tooltip message won't fit on left or right side and icon is relatively centered (or if window width is super small)
  if (
    innerWidth < messageWidthWithBuffer ||
    (isIconMostlyCentered &&
      innerWidth - right <= messageWidthWithBuffer &&
      innerWidth - left <= messageWidthWithBuffer)
  ) {
    return 'bottomCenter';
  }

  // if top of viewport
  if (top <= iconSize) {
    return 'bottom';
  }

  // if bottom of viewport
  if (innerHeight - bottom <= iconSize + buffer) {
    if (innerWidth - right <= messageWidthWithBuffer) {
      return 'topReverse';
    }
    return 'top';
  }

  // if on right and inbetween top and bottom off viewport
  if (innerWidth - right <= messageWidthWithBuffer) {
    return 'left';
  }

  // default orientation
  return 'right';
};

const Tooltip = ({ className = '', error = false, message }: TooltipTypes) => {
  const iconRef = useRef(null);
  const [messagePos, setMessagePos] = useState<ReturnType<typeof getMessageOrientation>>('right');
  const backupScreenWidthNum = 1200;
  const debounceDelayMS = 800;
  const [debouncedScreenWidth, setScreenWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : backupScreenWidthNum
  );

  const classes: {
    [key: string]: { arrow: string; inner: string; outer: string; rotation: number };
  } = {
    bottom: {
      arrow: 'ml-16 h-12',
      inner: '-translate-y-4',
      outer: 'js-bottom flex-col-reverse top-0 -ml-16 mt-16 group-hover:mt-24 group-focus:mt-24',
      rotation: 180,
    },
    bottomCenter: {
      arrow: 'mx-auto h-12',
      inner: '-translate-y-4',
      outer:
        'js-bottom flex-col-reverse top-0 -translate-x-1/2 left-1/2 group-hover:mt-24 group-focus:mt-24',
      rotation: 180,
    },
    left: {
      arrow: 'mt-16 w-12',
      inner: 'translate-x-4',
      outer: 'js-left flex-row right-0 -mt-32 mr-16 group-hover:mr-24 group-focus:mr-24',
      rotation: 270,
    },
    right: {
      arrow: 'mt-16 w-12',
      inner: '-translate-x-4',
      outer: 'js-right flex-row-reverse left-0 -mt-32 ml-16 group-hover:ml-24 group-focus:ml-24',
      rotation: 90,
    },
    top: {
      arrow: 'ml-16 h-12',
      inner: 'translate-y-4',
      outer: 'js-top flex-col bottom-0 -ml-16 mb-16 group-hover:mb-24 group-focus:mb-24',
      rotation: 0,
    },
    topReverse: {
      arrow: 'ml-16 h-12',
      inner: 'translate-y-4 translate-x-16',
      outer:
        'js-topReverse flex-col bottom-0 right-0 -ml-16 mb-24 group-hover:mb-24 group-focus:mb-24',
      rotation: 0,
    },
  };

  const setDebouncedWidth = useCallback(
    useDebounce(() => setScreenWidth(window?.innerWidth || backupScreenWidthNum), debounceDelayMS),
    []
  );

  const { arrow, inner, outer, rotation } = classes[messagePos];
  useEffect(() => {
    window.addEventListener('resize', setDebouncedWidth);
    return () => {
      window.removeEventListener('resize', setDebouncedWidth);
    };
  }, []);

  useEffect(() => {
    if (iconRef.current) {
      setMessagePos(getMessageOrientation(iconRef.current));
    }
  }, [debouncedScreenWidth]);

  if (!message) return null;

  return (
    <button
      className={`js-tooltip relative cursor-default group ${className}`}
      type="button"
      aria-label={message}
      ref={iconRef}
    >
      <SvgLoader
        aria-hidden="true"
        className="flex-none icon-18"
        data-testid="svg-loader"
        color={error ? 'text-red' : 'text-teal-darker'}
        focusable="false"
        name="Info"
      />
      <div
        className={`absolute z-tooltip flex items-start invisible group-hover:visible group-focus:visible opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-all delay-200 ${outer}`}
        style={{ width: '240px' }}
      >
        <div
          className={`js-tooltip-message rich-text text-left flex-1 p-16 transform text-white ${inner} ${
            error ? 'bg-red' : 'bg-teal-darker'
          }`}
        >
          {message}
        </div>
        <SvgLoader
          aria-hidden="true"
          className={`flex-shrink-0 ${arrow}`}
          color={error ? 'text-red' : 'text-teal-darker'}
          focusable="false"
          name="SmallTriangle"
          rotate={rotation}
          style={messagePos === 'topReverse' ? { marginLeft: '223px' } : {}}
        />
      </div>
    </button>
  );
};

export default Tooltip;
